import React, {useMemo} from "react";
import moment from "moment";
import {Box, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Calendar03Icon, Call02Icon, CheckmarkCircle02Icon, PlayIcon} from "@hugeicons/core-free-icons";
import {Panel} from "../../../dashboard/components/Panel";
import {campaignEnd, campaignStart, campaignState} from "./calendarUtils";

const count = (campaign, status) => Number(campaign.statusCounts?.[status]) || 0;

const StatTile = ({icon, color, label, value, note}) => (
  <Panel sx={{p: 2, flexDirection: "row", alignItems: "center", gap: 1.5}}>
    <Box sx={{
      width: 40, height: 40, borderRadius: "12px", flexShrink: 0, display: "flex", alignItems: "center",
      justifyContent: "center", color, bgcolor: `${color}1F`,
    }}>
      <HugeiconsIcon icon={icon} size={19}/>
    </Box>
    <Box sx={{minWidth: 0}}>
      <Typography noWrap sx={{fontSize: 13, fontWeight: 500, color: "#6B6B80"}}>{label}</Typography>
      <Typography sx={{fontSize: 24, fontWeight: 700, lineHeight: 1.2, letterSpacing: "-0.02em", color: "#23264D"}}>
        {value.toLocaleString()}
      </Typography>
      {note && <Typography noWrap sx={{fontSize: 12, color: "#8A8AA0"}}>{note}</Typography>}
    </Box>
  </Panel>
);

export const CampaignStatTiles = ({campaigns, month}) => {
  const stats = useMemo(() => {
    const today = moment().startOf("day");
    const weekEnd = today.clone().add(6, "days");
    const monthStart = month.clone().startOf("month");
    const monthEnd = month.clone().endOf("month");
    const running = campaigns.filter(campaign => campaignState(campaign) === "RUNNING");
    const startingSoon = campaigns.filter(campaign => {
      const start = campaignStart(campaign);
      return campaignState(campaign) === "SCHEDULED" && !start.isAfter(weekEnd, "day");
    });
    const open = campaigns.filter(campaign => campaignState(campaign) !== "FINISHED");
    const inMonth = campaigns.filter(campaign =>
      !campaignEnd(campaign).isBefore(monthStart, "day") && !campaignStart(campaign).isAfter(monthEnd, "day"));
    return {
      running: running.length,
      startingSoon: startingSoon.length,
      toCall: open.reduce((sum, campaign) => sum + count(campaign, "NOT_CALLED") + count(campaign, "QUEUED"), 0),
      answered: inMonth.reduce((sum, campaign) => sum + count(campaign, "ANSWERED"), 0),
      failed: inMonth.reduce((sum, campaign) => sum + count(campaign, "FAILED"), 0),
    };
  }, [campaigns, month]);

  return (
    <Box sx={{display: "grid", gap: 2, gridTemplateColumns: {xs: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))"}}}>
      <StatTile icon={PlayIcon} color={"#2F9A6A"} label={"Running now"} value={stats.running}
                note={stats.running ? "Calling today" : "Nothing running"}/>
      <StatTile icon={Calendar03Icon} color={"#8491F7"} label={"Starting this week"} value={stats.startingSoon}
                note={"Next 7 days"}/>
      <StatTile icon={Call02Icon} color={"#D9A13A"} label={"Left to call"} value={stats.toCall}
                note={"Open campaigns"}/>
      <StatTile icon={CheckmarkCircle02Icon} color={"#3D86C6"} label={`Answered in ${month.format("MMMM")}`}
                value={stats.answered} note={`${stats.failed.toLocaleString()} failed`}/>
    </Box>
  );
};
